import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "@phosphor-icons/react";
import Marquee from "react-fast-marquee";

const HERO_BG =
  "https://images.unsplash.com/photo-1585314062340-f1a5a7c9328d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGJsYWNrJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODA0NjkyNTF8MA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative grain pt-20 pb-16 lg:pt-28 lg:pb-24 min-h-[90vh] flex flex-col justify-center overflow-hidden"
    >
      {/* Background */}
      <div
        className="absolute inset-0 z-0 bg-cover bg-center opacity-20 mix-blend-overlay"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#1a3aa8]/40 via-[#1a3aa8]/70 to-[#1a3aa8]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={0}
          className="flex items-center gap-3 mb-10"
        >
          <span className="eyebrow-dot" />
          <p
            data-testid="hero-eyebrow"
            className="font-display text-xs sm:text-sm font-bold tracking-[0.25em] uppercase text-white/80"
          >
            Agencia de Marketing Digital · Est. 2026
          </p>
        </motion.div>

        <motion.h1
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={1}
          data-testid="hero-headline"
          className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter uppercase leading-[0.95]"
        >
          Con nuestra <br className="hidden sm:block" />
          estrategia,
          <br />
          <span className="text-volt">te van a conocer.</span>
        </motion.h1>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={2}
          className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-end"
        >
          <p
            data-testid="hero-subtitle"
            className="md:col-span-7 lg:col-span-6 text-base md:text-lg font-medium text-white/85 leading-relaxed max-w-xl"
          >
            Preparamos tu web para llevarla a lo más alto. Diseño, SEO,
            contenido y campañas que convierten audiencias en clientes y
            clientes en historias de crecimiento.
          </p>

          <div
            className="md:col-span-5 lg:col-span-6 flex flex-wrap items-center gap-4 md:justify-end"
          >
            <a
              href="https://wa.me/34686881546?text=Hola%2C%20quiero%20m%C3%A1s%20informaci%C3%B3n"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-primary-cta"
              className="btn-volt text-sm px-7 py-4"
            >
              Pedir Información
              <ArrowUpRight size={18} weight="bold" />
            </a>
            <button
              data-testid="hero-secondary-cta"
              onClick={() =>
                document
                  .getElementById("portfolio")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="btn-ghost text-sm px-7 py-4"
            >
              Ver Portfolio
              <ArrowDown size={18} weight="bold" />
            </button>
          </div>
        </motion.div>

        {/* Bottom stats strip */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={3}
          className="mt-20 lg:mt-28 border-t border-white/20 pt-10 grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          {[
            { k: "+120", v: "Clientes Satisfechos" },
            { k: "+300", v: "Proyectos Realizados" },
            { k: "+8", v: "Años de Experiencia" },
            { k: "24/7", v: "Soporte Estratégico" },
          ].map((item, i) => (
            <div key={i} data-testid={`hero-stat-${i}`} className="flex flex-col gap-2">
              <span className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter">
                {item.k}
              </span>
              <span className="font-display text-[10px] md:text-xs font-bold tracking-[0.2em] uppercase text-zinc-500">
                {item.v}
              </span>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Marquee strip */}
      <div className="relative z-10 mt-16 lg:mt-24 border-y border-white/20 py-6 marquee-fade">
        <Marquee speed={50} gradient={false} pauseOnHover>
          {[
            "SEO",
            "Diseño Web",
            "Email Marketing",
            "Redes Sociales",
            "Link Building",
            "Analítica",
            "Contenido",
            "Estrategia",
            "Branding",
            "Conversión",
          ].map((t, i) => (
            <div
              key={i}
              className="flex items-center gap-10 px-10 font-display text-2xl md:text-3xl font-black uppercase tracking-tighter text-white/90"
            >
              <span>{t}</span>
              <span className="text-volt">✦</span>
            </div>
          ))}
        </Marquee>
      </div>
    </section>
  );
}
